import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchBar } from "../components/SearchBar";
import { IssueCard } from "../components/IssueCard";
import { useBookmarks } from "../hooks/useBookmarks";
import { issuesData } from "../content";
import { searchIssues, filterIssues } from "../utils/search";
import { categoryLabels } from "../types";
import type { Issue, SeverityLevel } from "../types";
import {
  Search,
  Library,
  Filter,
  AlertTriangle,
  Printer,
  ArrowRight,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

const severities: SeverityLevel[] = ["critical", "high", "medium", "low"];
const printerTypes = ["epson", "all"];

function SectionTitle({
  icon: Icon,
  eyebrow,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-6">
      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">
        <Icon className="h-4 w-4" />
        {eyebrow}
      </div>

      <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
        {title}
      </h2>

      <p className="mt-3 max-w-3xl text-sm leading-6 text-white/70 md:text-base">
        {description}
      </p>
    </div>
  );
}

function getSeverityBadgeClass(severity: string) {
  switch (severity) {
    case "critical":
      return "border-red-400/20 bg-red-500/10 text-red-300";
    case "high":
      return "border-orange-400/20 bg-orange-500/10 text-orange-300";
    case "medium":
      return "border-amber-400/20 bg-amber-500/10 text-amber-300";
    default:
      return "border-emerald-400/20 bg-emerald-500/10 text-emerald-300";
  }
}

export function Issues() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    category: searchParams.get("category") || "",
    severity: "",
    printerType: "",
  });

  const { toggleBookmark, isBookmarked } = useBookmarks();

  useEffect(() => {
    const category = searchParams.get("category");
    if (category) {
      setFilters((prev) => ({ ...prev, category }));
    }
  }, [searchParams]);

  const filteredIssues = useMemo(() => {
    let results = issuesData.issues;

    if (searchQuery) {
      results = searchIssues(results, searchQuery);
    }

    results = filterIssues(results, {
      category: filters.category,
      severity: filters.severity,
      printerType: filters.printerType,
    });

    return results;
  }, [searchQuery, filters]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));

    if (key === "category" && value) {
      setSearchParams({ category: value });
    } else if (key === "category" && !value) {
      setSearchParams({});
    }
  };

  const clearFilters = () => {
    setFilters({ category: "", severity: "", printerType: "" });
    setSearchParams({});
    setSearchQuery("");
  };

  const hasActiveFilters =
    !!filters.category ||
    !!filters.severity ||
    !!filters.printerType ||
    !!searchQuery;

  const activeCategoryLabel = filters.category
    ? categoryLabels[filters.category as keyof typeof categoryLabels] ||
      filters.category
    : null;

  return (
    <div className="min-h-screen bg-[#070b14] text-white">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        {/* HERO */}
        <section className="relative overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] p-6 md:p-10">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.16),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(217,70,239,0.12),transparent_30%)]" />

          <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-400/20 bg-fuchsia-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-fuchsia-200">
                <Library className="h-4 w-4" />
                Troubleshooting Library
              </div>

              <h1 className="mt-5 text-4xl font-black tracking-tight md:text-5xl">
                Browse the DTF issue library
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-white/72 md:text-base">
                Search documented DTF issues, symptoms, and fixes across print
                quality, white ink, powder, film handling, hardware, and more.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/85">
                  <span className="font-bold text-white">
                    {issuesData.issues.length}
                  </span>{" "}
                  total issues
                </div>

                {activeCategoryLabel ? (
                  <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3 text-sm font-semibold text-cyan-300">
                    {activeCategoryLabel}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {[
                {
                  title: "Need a fast match?",
                  description:
                    "Search by symptom, tag, or issue name to narrow results quickly.",
                },
                {
                  title: "Need better filtering?",
                  description:
                    "Use category, severity, and printer type together for tighter results.",
                },
                {
                  title: "Need common problems first?",
                  description:
                    "Start with high-frequency issue types before diving deeper.",
                },
                {
                  title: "Need saved references?",
                  description:
                    "Bookmark useful issues so repeat fixes stay easy to find later.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-[24px] border border-white/10 bg-white/[0.05] p-5"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-xl border border-emerald-300/20 bg-emerald-400/10 p-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                    </div>

                    <div>
                      <h3 className="text-sm font-bold text-white">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-white/70">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEARCH + FILTERS */}
        <section className="mt-8 rounded-[26px] border border-white/10 bg-white/[0.04] p-5 md:p-6">
          <SectionTitle
            icon={Search}
            eyebrow="Search"
            title="Find an issue fast"
            description="Search by issue name, symptom, tag, severity, or printer type to narrow down the library quickly."
          />

          <div className="max-w-3xl">
            <SearchBar
              placeholder="Search issues by name, symptom, or tag..."
              onSearch={setSearchQuery}
            />
          </div>

          <div className="mt-6">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
              <Filter className="h-4 w-4" />
              Filters
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_auto]">
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange("category", e.target.value)}
                className="min-w-0 rounded-2xl border border-white/10 bg-black/15 px-4 py-3 text-white outline-none transition focus:border-cyan-300/35 focus:bg-white/[0.04]"
              >
                <option value="">All Categories</option>
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>

              <select
                value={filters.severity}
                onChange={(e) => handleFilterChange("severity", e.target.value)}
                className="min-w-0 rounded-2xl border border-white/10 bg-black/15 px-4 py-3 text-white outline-none transition focus:border-cyan-300/35 focus:bg-white/[0.04]"
              >
                <option value="">All Severities</option>
                {severities.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>

              <select
                value={filters.printerType}
                onChange={(e) =>
                  handleFilterChange("printerType", e.target.value)
                }
                className="min-w-0 rounded-2xl border border-white/10 bg-black/15 px-4 py-3 text-white outline-none transition focus:border-cyan-300/35 focus:bg-white/[0.04]"
              >
                <option value="">All Printer Types</option>
                {printerTypes.map((t) => (
                  <option key={t} value={t}>
                    {t === "all" ? "Universal" : t.toUpperCase()}
                  </option>
                ))}
              </select>

              {hasActiveFilters ? (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300/25 bg-cyan-400/10 px-5 py-3 text-sm font-semibold text-cyan-300 transition hover:border-cyan-300/40 hover:bg-cyan-400/15"
                >
                  Clear Filters
                </button>
              ) : (
                <div className="hidden xl:block" />
              )}
            </div>
          </div>
        </section>

        {/* RESULTS BAR */}
        <section className="mt-6 rounded-[24px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-white/68">Showing results</p>
              <p className="mt-1 text-lg font-bold text-white">
                {filteredIssues.length} of {issuesData.issues.length} issues
              </p>
            </div>

            {hasActiveFilters ? (
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/20 bg-amber-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">
                <AlertTriangle className="h-4 w-4" />
                Filters Active
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-300">
                <Printer className="h-4 w-4" />
                Full Library Visible
              </div>
            )}
          </div>
        </section>

        {/* GRID */}
        {filteredIssues.length > 0 ? (
          <section className="mt-6">
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredIssues.map((issue: Issue) => (
                <div
                  key={issue.id}
                  className="rounded-[24px] transition duration-300 hover:-translate-y-1"
                >
                  <IssueCard
                    issue={issue}
                    isBookmarked={isBookmarked(issue.id, "issue")}
                    onToggleBookmark={() =>
                      toggleBookmark({
                        id: issue.id,
                        type: "issue",
                        title: issue.title,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </section>
        ) : (
          <section className="mt-8 rounded-[26px] border border-white/10 bg-white/[0.04] p-10 text-center">
            <div className="mx-auto max-w-xl">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-cyan-300/15 bg-cyan-400/10">
                <Search className="h-8 w-8 text-cyan-300" />
              </div>

              <h3 className="mt-5 text-2xl font-extrabold tracking-tight text-white">
                No issues found
              </h3>

              <p className="mt-3 text-sm leading-6 text-white/68 md:text-base">
                Try a broader search, remove some filters, or clear everything to
                view the full issue library again.
              </p>

              <button
                onClick={clearFilters}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300/35 bg-cyan-400/20 px-6 py-3 text-sm font-semibold text-cyan-100 transition hover:-translate-y-0.5 hover:border-cyan-200/60 hover:bg-cyan-400/30"
              >
                Clear Filters
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </section>
        )}

        {/* ACTIVE FILTER TAGS */}
        {hasActiveFilters ? (
          <section className="mt-6">
            <div className="flex flex-wrap gap-2">
              {searchQuery ? (
                <span className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold text-cyan-300">
                  Search: {searchQuery}
                </span>
              ) : null}

              {filters.category ? (
                <span className="rounded-full border border-fuchsia-400/20 bg-fuchsia-500/10 px-3 py-1 text-xs font-semibold text-fuchsia-200">
                  Category: {activeCategoryLabel}
                </span>
              ) : null}

              {filters.severity ? (
                <span
                  className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase ${getSeverityBadgeClass(
                    filters.severity
                  )}`}
                >
                  Severity: {filters.severity}
                </span>
              ) : null}

              {filters.printerType ? (
                <span className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300">
                  Printer:{" "}
                  {filters.printerType === "all"
                    ? "Universal"
                    : filters.printerType.toUpperCase()}
                </span>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}